/**
 * Audit Logs Page
 * Displays platform-level audit logs with filtering and search
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminAuth } from '../auth/AdminAuthContext';
import RetentionPanel from './RetentionPanel';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Search,
  Filter,
  Calendar,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  Shield,
  UserCog,
  Building2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Package,
  CreditCard,
  Gauge,
  Rocket,
  KeyRound,
  Settings,
  Trash2,
  Download,
  ArrowUpDown,
} from 'lucide-react';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Action type to icon/color mapping. Covers every action string that
// actually has a real call site in the backend (see AUDIT_ACTIONS /
// audit_log_service.py) — anything else falls back to a generic clock
// icon with a title-cased label via getActionConfig() below.
const actionConfig = {
  admin_login: { icon: Shield, color: 'bg-green-100 text-green-800', label: 'Admin Login' },
  admin_login_failed: { icon: XCircle, color: 'bg-red-100 text-red-800', label: 'Login Failed' },
  admin_logout: { icon: Shield, color: 'bg-gray-100 text-gray-800', label: 'Admin Logout' },

  tenant_created: { icon: Building2, color: 'bg-blue-100 text-blue-800', label: 'Tenant Created' },
  tenant_updated: { icon: Building2, color: 'bg-blue-100 text-blue-800', label: 'Tenant Updated' },
  tenant_suspended: { icon: AlertTriangle, color: 'bg-orange-100 text-orange-800', label: 'Tenant Suspended' },
  tenant_activated: { icon: CheckCircle, color: 'bg-green-100 text-green-800', label: 'Tenant Activated' },
  tenant_deleted: { icon: Trash2, color: 'bg-red-100 text-red-800', label: 'Tenant Deleted (soft)' },
  tenant_hard_deleted: { icon: Trash2, color: 'bg-red-200 text-red-900', label: 'Tenant Permanently Deleted' },
  tenant_suspend: { icon: AlertTriangle, color: 'bg-orange-100 text-orange-800', label: 'Tenant Suspended (support)' },
  tenant_reactivate: { icon: CheckCircle, color: 'bg-green-100 text-green-800', label: 'Tenant Reactivated (support)' },
  tenant_extend_trial: { icon: Rocket, color: 'bg-purple-100 text-purple-800', label: 'Trial Extended (support)' },
  tenant_resend_welcome: { icon: Rocket, color: 'bg-purple-100 text-purple-800', label: 'Welcome Email Resent (support)' },
  tenant_reset_admin: { icon: KeyRound, color: 'bg-yellow-100 text-yellow-800', label: 'Tenant Admin Password Reset (support)' },
  tenant_create_payment_link: { icon: CreditCard, color: 'bg-purple-100 text-purple-800', label: 'Payment Link Created (support)' },
  tenant_retry_provisioning: { icon: Rocket, color: 'bg-purple-100 text-purple-800', label: 'Provisioning Retried (support)' },

  plan_assigned: { icon: CreditCard, color: 'bg-purple-100 text-purple-800', label: 'Plan Assigned' },
  plan_created: { icon: CreditCard, color: 'bg-purple-100 text-purple-800', label: 'Plan Created' },
  plan_data_synced: { icon: RefreshCw, color: 'bg-indigo-100 text-indigo-800', label: 'Plan Data Synced' },
  plan_changed: { icon: CreditCard, color: 'bg-purple-100 text-purple-800', label: 'Plan Changed' },
  plan_manual_override: { icon: CreditCard, color: 'bg-purple-100 text-purple-800', label: 'Plan Manually Overridden' },
  subscription_cancelled: { icon: XCircle, color: 'bg-red-100 text-red-800', label: 'Subscription Cancelled' },
  billing_updated: { icon: CreditCard, color: 'bg-purple-100 text-purple-800', label: 'Billing Updated' },
  seat_limit_changed: { icon: UserCog, color: 'bg-yellow-100 text-yellow-800', label: 'Seat Limit Changed' },

  modules_updated: { icon: Package, color: 'bg-indigo-100 text-indigo-800', label: 'Modules Updated' },
  module_enabled: { icon: Package, color: 'bg-green-100 text-green-800', label: 'Module Enabled' },
  module_disabled: { icon: Package, color: 'bg-gray-100 text-gray-800', label: 'Module Disabled' },

  limit_updated: { icon: Gauge, color: 'bg-yellow-100 text-yellow-800', label: 'Limit Updated' },
  limit_override_removed: { icon: Gauge, color: 'bg-gray-100 text-gray-800', label: 'Limit Override Removed' },
  limits_initialized: { icon: Gauge, color: 'bg-indigo-100 text-indigo-800', label: 'Limits Initialized' },

  user_created: { icon: UserCog, color: 'bg-green-100 text-green-800', label: 'User Created' },
  user_updated: { icon: UserCog, color: 'bg-blue-100 text-blue-800', label: 'User Updated' },
  user_suspended: { icon: UserCog, color: 'bg-orange-100 text-orange-800', label: 'User Suspended' },
  user_activated: { icon: UserCog, color: 'bg-green-100 text-green-800', label: 'User Activated' },
  user_deleted: { icon: UserCog, color: 'bg-red-100 text-red-800', label: 'User Deleted' },
  user_password_reset: { icon: KeyRound, color: 'bg-yellow-100 text-yellow-800', label: 'Password Reset' },

  license_catalog_created: { icon: Package, color: 'bg-green-100 text-green-800', label: 'License Created' },
  license_catalog_updated: { icon: Package, color: 'bg-blue-100 text-blue-800', label: 'License Updated' },
  license_catalog_deleted: { icon: Package, color: 'bg-red-100 text-red-800', label: 'License Deactivated' },
  tenant_license_added: { icon: Package, color: 'bg-green-100 text-green-800', label: 'License Added to Tenant' },
  tenant_license_updated: { icon: Package, color: 'bg-blue-100 text-blue-800', label: 'Tenant License Updated' },
  tenant_license_removed: { icon: Package, color: 'bg-red-100 text-red-800', label: 'License Removed from Tenant' },
  user_license_assigned: { icon: UserCog, color: 'bg-green-100 text-green-800', label: 'License Assigned to User' },
  user_license_revoked: { icon: UserCog, color: 'bg-red-100 text-red-800', label: 'License Revoked from User' },

  release_created: { icon: Rocket, color: 'bg-blue-100 text-blue-800', label: 'Release Created' },
  release_updated: { icon: Rocket, color: 'bg-blue-100 text-blue-800', label: 'Release Updated' },
  release_set_default_for_new_tenants: { icon: Rocket, color: 'bg-indigo-100 text-indigo-800', label: 'Release Set as Default' },
  tenant_version_assigned: { icon: Rocket, color: 'bg-blue-100 text-blue-800', label: 'Version Assigned' },
  tenant_version_changed: { icon: Rocket, color: 'bg-blue-100 text-blue-800', label: 'Version Changed' },
  tenant_upgrade_started: { icon: Rocket, color: 'bg-yellow-100 text-yellow-800', label: 'Upgrade Started' },
  tenant_upgrade_completed: { icon: CheckCircle, color: 'bg-green-100 text-green-800', label: 'Upgrade Completed' },
  tenant_upgrade_failed: { icon: XCircle, color: 'bg-red-100 text-red-800', label: 'Upgrade Failed' },

  provisioning_job_created: { icon: Rocket, color: 'bg-blue-100 text-blue-800', label: 'Provisioning Job Created' },
  provisioning_job_completed: { icon: CheckCircle, color: 'bg-green-100 text-green-800', label: 'Provisioning Job Completed' },
  provisioning_job_failed: { icon: XCircle, color: 'bg-red-100 text-red-800', label: 'Provisioning Job Failed' },
  provisioning_job_retried: { icon: RefreshCw, color: 'bg-yellow-100 text-yellow-800', label: 'Provisioning Job Retried' },
  provisioning_job_cancelled: { icon: XCircle, color: 'bg-gray-100 text-gray-800', label: 'Provisioning Job Cancelled' },

  settings_updated: { icon: Settings, color: 'bg-blue-100 text-blue-800', label: 'Settings Updated' },
  audit_logs_purged: { icon: Trash2, color: 'bg-gray-100 text-gray-800', label: 'Audit Logs Purged' },
};

/**
 * How long this event is kept, and why. `security` is the one that matters:
 * it is never removed by retention at any setting, and an operator who assumes
 * a shorter window will eventually clear sign-in and impersonation history
 * would find out at the worst possible moment.
 */
const RETENTION_TONE = {
  security: { label: 'Security', className: 'bg-rose-50 text-rose-700 border-rose-200',
              title: 'Protected — never removed by retention, at any setting' },
  administrative: { label: 'Administrative', className: 'bg-amber-50 text-amber-800 border-amber-200',
                    title: 'Kept for at least 24 months regardless of policy' },
  operational: { label: 'Operational', className: 'bg-slate-100 text-slate-600 border-slate-200',
                 title: 'Removed once past the retention window' },
};

const RetentionBadge = ({ value }) => {
  const tone = RETENTION_TONE[value];
  if (!tone) {
    // Historical rows written before classification existed. Reported as
    // unclassified rather than guessed at — and they are retained, because
    // unknown resolves to keep.
    return (
      <Badge variant="outline" className="text-slate-400" title="Written before classification; retained">
        Unclassified
      </Badge>
    );
  }
  return <Badge variant="outline" className={tone.className} title={tone.title}>{tone.label}</Badge>;
};

/**
 * Who acted. `unknown` is deliberately not softened into "system" or attributed
 * to the tenant — "we do not know who did this" is its own answer.
 */
const ACTOR_TONE = {
  platform_operator: { label: 'Platform operator', className: 'text-indigo-600' },
  system: { label: 'System', className: 'text-slate-500' },
  tenant_user: { label: 'Tenant user', className: 'text-emerald-600' },
  unknown: { label: 'Actor not recorded', className: 'text-amber-600' },
};

const ActorTypeBadge = ({ value }) => {
  const tone = ACTOR_TONE[value];
  if (!tone) return <span className="text-xs text-slate-400">—</span>;
  return <span className={`text-xs ${tone.className}`}>{tone.label}</span>;
};

const AuditLogsPage = () => {
  const { getAdminToken } = useAdminAuth();
  // The page never read its own query string, so the deep link from Tenant
  // Usage (`/audit-logs?tenant_id=...`) landed unfiltered — it looked like
  // the tenant had no audit history.
  const [searchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  // 10/25/50, matching the other finalized Admin lists. The old page size was
  // 20 with no setter.
  const [limit, setLimit] = useState(25);
  const [sortDesc, setSortDesc] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  // A view default, not a retention policy. The authoritative history goes back
  // as far as the retention classes allow — security events forever — and
  // "Custom" or "All time" reaches it.
  const [rangePreset, setRangePreset] = useState('30d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [tenantIdFilter, setTenantIdFilter] = useState(searchParams.get('tenant_id') || '');
  const [actorIdFilter, setActorIdFilter] = useState(searchParams.get('actor_id') || '');
  const [actorTypeFilter, setActorTypeFilter] = useState('');
  const [retentionFilter, setRetentionFilter] = useState('');
  const [targetTypeFilter, setTargetTypeFilter] = useState('');

  // Detail modal
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Set when a fetch fails outright (network/5xx) so the table can show a
  // real error state instead of silently looking empty or stale.
  // Holds the reason, not a boolean — the page has to be able to say which
  // kind of failure this was.
  const [loadError, setLoadError] = useState(null);

  const fetchActionTypes = useCallback(async () => {
    const token = getAdminToken();
    try {
      const response = await fetch(`${API_URL}/api/admin/audit-logs/action-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setActionTypes(data);
      } else {
        toast.error('Failed to load action type filters');
      }
    } catch (error) {
      console.error('Failed to fetch action types:', error);
      toast.error('Failed to load action type filters');
    }
  }, [getAdminToken]);

  // The preset resolves to concrete bounds at query time so the server does the
  // filtering. Nothing is fetched and then discarded in the browser.
  const resolvedRange = useMemo(() => {
    if (rangePreset === 'custom') {
      return {
        start: startDate ? new Date(startDate).toISOString() : null,
        end: endDate ? new Date(endDate).toISOString() : null,
      };
    }
    if (rangePreset === 'all') return { start: null, end: null };
    const days = { today: 0, '7d': 7, '30d': 30 }[rangePreset] ?? 30;
    const start = new Date();
    if (days === 0) start.setHours(0, 0, 0, 0);
    else start.setDate(start.getDate() - days);
    return { start: start.toISOString(), end: null };
  }, [rangePreset, startDate, endDate]);

  // One place builds the query, and the export uses it too — an export that
  // built its own would drift from what the operator is looking at.
  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (actionFilter) params.append('action', actionFilter);
    if (resolvedRange.start) params.append('start_date', resolvedRange.start);
    if (resolvedRange.end) params.append('end_date', resolvedRange.end);
    if (tenantIdFilter) params.append('tenant_id', tenantIdFilter);
    if (actorIdFilter) params.append('actor_id', actorIdFilter);
    if (actorTypeFilter) params.append('actor_type', actorTypeFilter);
    if (retentionFilter) params.append('retention_class', retentionFilter);
    if (targetTypeFilter) params.append('target_type', targetTypeFilter);
    return params;
  }, [search, actionFilter, resolvedRange, tenantIdFilter, actorIdFilter,
      actorTypeFilter, retentionFilter, targetTypeFilter]);

  const fetchLogs = useCallback(async () => {
    const token = getAdminToken();
    setLoading(true);
    setLoadError(null);
    try {
      const params = buildParams();
      params.append('skip', String(page * limit));
      params.append('limit', String(limit));
      params.append('sort', sortDesc ? 'desc' : 'asc');

      const response = await fetch(`${API_URL}/api/admin/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      } else {
        // The server answered and refused. That is a real failure and worth a
        // toast — unlike a restart, which is not.
        setLoadError(response.status === 401
          ? 'Your session has expired. Sign in again.'
          : `The server refused this request (HTTP ${response.status}).`);
        toast.error('Failed to load audit logs');
      }
    } catch (error) {
      // The server was not reached at all. During a deploy the backend restarts
      // for a minute or so, and reporting that as "couldn't load audit logs"
      // makes a routine restart look like a broken feature — which is exactly
      // how it was first reported. Say which of the two it is, and do not
      // shout about the recoverable one.
      console.error('Failed to fetch audit logs:', error);
      setLoadError('Could not reach the server. It may be restarting after a deploy — retry in a moment.');
    } finally {
      setLoading(false);
    }
  }, [getAdminToken, page, limit, sortDesc, buildParams]);

  const exportLogs = useCallback(async () => {
    const token = getAdminToken();
    setExporting(true);
    try {
      // The SAME filters as the table. The server re-checks authorization and
      // audits the export with its scope.
      const params = buildParams();
      params.append('limit', '50000');
      const response = await fetch(`${API_URL}/api/admin/audit-logs/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        toast.error(response.status === 403
          ? 'You do not have permission to export the audit log.'
          : `Export failed (HTTP ${response.status}).`);
        return;
      }
      const data = await response.json();
      const escape = (value) => {
        if (value === null || value === undefined) return '';
        const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
        return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
      };
      const csv = [
        data.columns.join(','),
        ...data.rows.map((row) => data.columns.map((c) => escape(row[c])).join(',')),
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cluvik-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      // Truncation is stated, never silent — a short file that does not say so
      // reads as the complete answer to the question that was asked.
      if (data.truncated) {
        toast.success(
          `Exported ${data.exported.toLocaleString()} of ${data.matched.toLocaleString()} matching rows. ` +
          `Narrow the filters to export the rest.`,
          { duration: 8000 }
        );
      } else {
        toast.success(`Exported ${data.exported.toLocaleString()} rows.`);
      }
    } catch (err) {
      toast.error('Export could not reach the server.');
    } finally {
      setExporting(false);
    }
  }, [getAdminToken, buildParams]);

  useEffect(() => {
    fetchActionTypes();
  }, [fetchActionTypes]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchLogs();
  };

  const clearFilters = () => {
    setSearch('');
    setActionFilter('');
    setRangePreset('30d');
    setStartDate('');
    setEndDate('');
    setTenantIdFilter('');
    setActorIdFilter('');
    setActorTypeFilter('');
    setRetentionFilter('');
    setTargetTypeFilter('');
    setPage(0);
  };

  const activeFilterCount = [
    search, actionFilter, tenantIdFilter, actorIdFilter,
    actorTypeFilter, retentionFilter, targetTypeFilter,
  ].filter(Boolean).length + (rangePreset !== '30d' ? 1 : 0);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getActionConfig = (action) => {
    return actionConfig[action] || { 
      icon: Clock, 
      color: 'bg-gray-100 text-gray-800', 
      label: action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) 
    };
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Audit Logs</h1>
          <p className="text-slate-600">Track platform-level actions and security events</p>
        </div>
        <Button onClick={fetchLogs} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Retention and freeze. Read by anyone with audit:read; changed only
          with audit:purge, and every change is itself an audit event. */}
      <RetentionPanel />

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search logs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="audit-search-input"
              />
            </div>

            {/* Action Type Filter */}
            <Select value={actionFilter} onValueChange={(value) => setActionFilter(value === 'all' ? '' : value)}>
              <SelectTrigger data-testid="audit-action-filter">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {actionTypes.map((type) => (
                  <SelectItem key={type.action} value={type.action}>
                    {type.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Date range. A VIEW default of 30 days — the authoritative
                history is longer and security events are never removed, so
                "All time" reaches what retention actually kept. */}
            <Select value={rangePreset} onValueChange={(v) => { setRangePreset(v); setPage(0); }}>
              <SelectTrigger data-testid="audit-range-preset">
                <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>

            {rangePreset === 'custom' && (
              <>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="datetime-local"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="pl-10"
                    data-testid="audit-start-date"
                  />
                </div>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="datetime-local"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="pl-10"
                    data-testid="audit-end-date"
                  />
                </div>
              </>
            )}

            {/* Who acted, and how long the row is kept — both were rendered as
                badges with no way to ask for more like them. */}
            <Select value={actorTypeFilter || 'all'} onValueChange={(v) => { setActorTypeFilter(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger data-testid="audit-actor-type-filter">
                <SelectValue placeholder="Any actor type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any actor type</SelectItem>
                <SelectItem value="platform_operator">Platform operator</SelectItem>
                <SelectItem value="tenant_user">Tenant user</SelectItem>
                <SelectItem value="system">System</SelectItem>
                <SelectItem value="unknown">Actor not recorded</SelectItem>
              </SelectContent>
            </Select>

            <Select value={retentionFilter || 'all'} onValueChange={(v) => { setRetentionFilter(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger data-testid="audit-retention-filter">
                <SelectValue placeholder="Any retention class" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any retention class</SelectItem>
                <SelectItem value="security">Security</SelectItem>
                <SelectItem value="administrative">Administrative</SelectItem>
                <SelectItem value="operational">Operational</SelectItem>
              </SelectContent>
            </Select>

            {/* Tenant ID */}
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Tenant ID..."
                value={tenantIdFilter}
                onChange={(e) => setTenantIdFilter(e.target.value)}
                className="pl-10 font-mono text-sm"
                data-testid="audit-tenant-filter"
              />
            </div>

            {/* Actor ID */}
            <div className="relative">
              <UserCog className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Actor (admin) ID..."
                value={actorIdFilter}
                onChange={(e) => setActorIdFilter(e.target.value)}
                className="pl-10 font-mono text-sm"
                data-testid="audit-actor-filter"
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-2">
              <Button type="submit" className="flex-1" data-testid="audit-search-btn">
                Search
              </Button>
              {activeFilterCount > 0 && (
                <Button type="button" variant="outline" onClick={clearFilters}>
                  Clear{activeFilterCount > 1 ? ` (${activeFilterCount})` : ''}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={exportLogs}
                disabled={exporting}
                title="Exports exactly what these filters select"
                data-testid="audit-export-btn"
              >
                {exporting
                  ? <RefreshCw className="h-4 w-4 animate-spin" />
                  : <Download className="h-4 w-4" />}
                <span className="ml-2 hidden sm:inline">Export</span>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Audit Events</CardTitle>
            <Badge variant="secondary">{total} total logs</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : loadError ? (
            <div className="text-center py-12 text-slate-500">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-red-400" />
              <p className="font-medium text-slate-700">Couldn't load audit logs</p>
              <p className="mt-1 text-sm max-w-md mx-auto">{loadError}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={fetchLogs}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No audit logs found matching your criteria
            </div>
          ) : (
            <>
              {/* The table scrolls inside its own container rather than the
                  page scrolling sideways, and the two lowest-value columns
                  drop below md so a phone shows action / actor / target /
                  time without a horizontal scrub. */}
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Action</TableHead>
                    <TableHead className="hidden lg:table-cell">Retention</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead className="hidden md:table-cell">Tenant</TableHead>
                    <TableHead>
                      <button
                        type="button"
                        onClick={() => { setSortDesc((v) => !v); setPage(0); }}
                        className="inline-flex items-center gap-1 hover:text-slate-900"
                        data-testid="audit-sort-timestamp"
                        title={sortDesc ? 'Newest first' : 'Oldest first'}
                      >
                        Timestamp
                        <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => {
                    const config = getActionConfig(log.action);
                    const IconComponent = config.icon;
                    
                    return (
                      <TableRow key={log.id} data-testid={`audit-log-${log.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded ${config.color}`}>
                              <IconComponent className="h-4 w-4" />
                            </div>
                            <span className="font-medium">{config.label}</span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <RetentionBadge value={log.retention_class} />
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div className="font-medium">
                              {log.actor_email || <span className="text-slate-400">Not recorded</span>}
                            </div>
                            <ActorTypeBadge value={log.actor_type} />
                          </div>
                        </TableCell>
                        {/* WHAT was acted on. This column could not exist
                            before: target_id was the tenant's id on every user
                            event, so it would have named the tenant for every
                            user action ever taken. */}
                        <TableCell>
                          {log.entity_name || log.target_id ? (
                            <div className="text-sm">
                              <div>{log.entity_name || <span className="font-mono text-xs">{String(log.target_id).slice(0, 8)}…</span>}</div>
                              {log.target_type && (
                                <span className="text-xs text-slate-400 capitalize">{log.target_type.replace(/_/g, ' ')}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {log.tenant_id ? (
                            <Badge variant="outline" className="font-mono text-xs">
                              {log.tenant_id.slice(0, 8)}...
                            </Badge>
                          ) : (
                            <span className="text-slate-400">System</span>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {formatDate(log.timestamp)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedLog(log);
                              setDetailOpen(true);
                            }}
                            data-testid={`view-log-${log.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </div>

              {/* Pagination */}
              <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t">
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <span>
                    Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total}
                  </span>
                  <Select
                    value={String(limit)}
                    onValueChange={(v) => { setLimit(Number(v)); setPage(0); }}
                  >
                    <SelectTrigger className="w-[110px] h-8" data-testid="audit-page-size">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 / page</SelectItem>
                      <SelectItem value="25">25 / page</SelectItem>
                      <SelectItem value="50">50 / page</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    data-testid="audit-prev-page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-slate-600">
                    Page {page + 1} of {totalPages || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= totalPages - 1}
                    data-testid="audit-next-page"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Audit Log Details</DialogTitle>
            <DialogDescription>
              Complete information about this audit event
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-500">Action</label>
                  <p className="font-medium">{selectedLog.action_description || selectedLog.action}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">Timestamp</label>
                  <p className="font-medium">{formatDate(selectedLog.timestamp)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">Actor Email</label>
                  <p className="font-medium">{selectedLog.actor_email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">Actor ID</label>
                  <p className="font-mono text-sm">{selectedLog.actor_id || '—'}</p>
                </div>
                {/* Now that the target is stated rather than derived, it is
                    worth showing: it used to be the tenant's id on every user
                    event. */}
                {selectedLog.target_id && (
                  <div>
                    <label className="text-sm font-medium text-slate-500">Target ID</label>
                    <p className="font-mono text-sm">{selectedLog.target_id}</p>
                  </div>
                )}
                {/* HOW the action arrived, which is a different question from
                    who. Absent means not recorded, and says so. */}
                <div>
                  <label className="text-sm font-medium text-slate-500">Source</label>
                  <p className="font-medium capitalize">
                    {/* Rows written before the two writers converged carry the
                        service name here, not a source. Shown as unrecorded
                        rather than as a source nobody can filter by. */}
                    {selectedLog.source && selectedLog.source !== 'cluvik-admin'
                      ? String(selectedLog.source).replace(/_/g, ' ')
                      : <span className="text-slate-400">Not recorded</span>}
                  </p>
                </div>
                {selectedLog.result && (
                  <div>
                    <label className="text-sm font-medium text-slate-500">Result</label>
                    <p className="font-medium capitalize">{selectedLog.result}</p>
                  </div>
                )}
                {selectedLog.impersonated_user_id && (
                  <div className="col-span-2 rounded border border-amber-200 bg-amber-50 px-3 py-2">
                    <label className="text-sm font-medium text-amber-900">Acting as another user</label>
                    <p className="text-sm text-amber-900">
                      The administrator above performed this while impersonating{' '}
                      <span className="font-mono">{selectedLog.impersonated_user_id}</span>.
                      Both identities are recorded.
                    </p>
                  </div>
                )}
                {selectedLog.correlation_id && (
                  <div>
                    <label className="text-sm font-medium text-slate-500">Correlation ID</label>
                    <p className="font-mono text-xs">{selectedLog.correlation_id}</p>
                  </div>
                )}
                {selectedLog.tenant_id && (
                  <div>
                    <label className="text-sm font-medium text-slate-500">Tenant ID</label>
                    <p className="font-mono text-sm">{selectedLog.tenant_id}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-slate-500">IP Address</label>
                  {selectedLog.ip_address ? (
                    <p className="font-mono text-sm">{selectedLog.ip_address}</p>
                  ) : (
                    // Hiding the row entirely made "no user request stood
                    // behind this" indistinguishable from "this field does not
                    // apply". A background job or a scheduler tick has no
                    // caller, and that is worth saying.
                    <p className="text-sm text-slate-400">
                      Not recorded — no user request behind this event, or predates IP capture
                    </p>
                  )}
                </div>
                {selectedLog.module_name && (
                  <div>
                    <label className="text-sm font-medium text-slate-500">Module</label>
                    <p className="font-medium">{selectedLog.module_name}</p>
                  </div>
                )}
                {selectedLog.entity_name && (
                  <div>
                    <label className="text-sm font-medium text-slate-500">Entity</label>
                    <p className="font-medium">{selectedLog.entity_name}</p>
                  </div>
                )}
                {selectedLog.target_type && (
                  <div>
                    <label className="text-sm font-medium text-slate-500">Target Type</label>
                    <p className="font-medium">{selectedLog.target_type}</p>
                  </div>
                )}
                {selectedLog.reason && (
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-slate-500">Reason</label>
                    <p className="font-medium">{selectedLog.reason}</p>
                  </div>
                )}
              </div>

              {(selectedLog.old_value || selectedLog.new_value) && (
                <div>
                  <label className="text-sm font-medium text-slate-500">Change</label>
                  <div className="mt-1 grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Before</p>
                      <pre className="p-3 bg-red-50 border border-red-100 rounded-lg text-sm overflow-auto max-h-48">
                        {selectedLog.old_value ? JSON.stringify(selectedLog.old_value, null, 2) : '(none)'}
                      </pre>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">After</p>
                      <pre className="p-3 bg-green-50 border border-green-100 rounded-lg text-sm overflow-auto max-h-48">
                        {selectedLog.new_value ? JSON.stringify(selectedLog.new_value, null, 2) : '(none)'}
                      </pre>
                    </div>
                  </div>
                </div>
              )}

              {/* The raw record, collapsed. It is genuinely useful — but as
                  the technical section an operator opens, not as the primary
                  reading experience. Everything above is the same data laid
                  out to be read. */}
              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <details className="rounded border border-slate-200">
                  <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
                    Technical detail
                  </summary>
                  <pre className="p-3 bg-slate-50 text-xs overflow-auto max-h-60">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                  <p className="px-3 pb-2 text-xs text-slate-400">
                    Secrets are replaced with [redacted] before an audit row is written.
                  </p>
                </details>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogsPage;
